import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { MdQrCodeScanner } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  Add,
  Refresh,
  Print,
  CheckCircle,
  Sell,
  Inventory2,
  Autorenew,
} from "@mui/icons-material";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";
import YetkisizErisim from "./components/YetkisizErisim";
import ProsetDataGrid from "./components/KuyumcuDataGrid";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuBarkodluIslemlerPage.css";

const getTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getNowDateTimeLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const formatDateTimeTR = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("tr-TR") +
    " " +
    d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    })
  );
};

const formatNumberTR = (value, fraction = 2) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("tr-TR", {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  });
};

const safeNumber = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  return Number.isNaN(n) ? "" : n;
};

const KuyumcuBarkodluIslemlerPage = () => {
  const navigate = useNavigate();

  const userId = getUserId();
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const kuyumcuTipi =
    localStorage.getItem("kuyumcuTipi") ||
    sessionStorage.getItem("kuyumcuTipi");

  const uyelikTipi =
    localStorage.getItem("uyelikTip") || sessionStorage.getItem("uyelikTip");

  const barkodInputRef = useRef(null);

  useEffect(() => {
    if (!userId || !token) navigate("/login");
  }, [userId, token, navigate]);

  /* =========================================================
     YETKİLER
  ========================================================= */
  const [yetkiler, setYetkiler] = useState(null);
  const [yetkisiz, setYetkisiz] = useState(false);

  const yetkiSet = useMemo(() => new Set(yetkiler || []), [yetkiler]);

  const hasPermission = (code) => {
    if (yetkiSet.has("ALL")) return true;
    return yetkiSet.has(code);
  };

  const loadYetkiler = async () => {
    if (!userId) return;
    try {
      const resp = await axiosInstance.get("/me/yetki-kodlari", {
        params: { girisYapanUserId: userId },
      });
      setYetkiler(resp.data || []);
    } catch (e) {
      console.error("Yetkiler alınamadı:", e);
      setYetkiler([]);
    }
  };

  useEffect(() => {
    if (!userId) return;
    loadYetkiler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (yetkiler === null) return;

    const barkodSayfasinaErisimVar =
      uyelikTipi === "SUPER_USER" ||
      kuyumcuTipi === "VITRIN_KUYUMCU" ||
      hasPermission("BARKODLU_ISLEMLER_VIEW");

    if (!barkodSayfasinaErisimVar) {
      setYetkisiz(true);
      return;
    }

    setYetkisiz(false);
  }, [yetkiler, uyelikTipi, kuyumcuTipi]);

  /* =========================================================
     GENEL STATE
  ========================================================= */
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saleLoading, setSaleLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [stokOptions, setStokOptions] = useState([]);
  const [hesapOptions, setHesapOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [testPrintLoading, setTestPrintLoading] = useState(false);

  /* =========================================================
     FİLTRELER
  ========================================================= */
  const [filters, setFilters] = useState({
    alisTarih: getTodayDate(),
    satisTarih: "",
    stokAdi: "",
  });

  /* =========================================================
     ALIŞ PANEL FORM
  ========================================================= */
  const emptyAlisForm = {
    tarih: getNowDateTimeLocal(),
    stokId: "",
    gram: "",
    ayar: "",
    milyem: "",
    olcu: "",
    iscilikAlis: "",
    iscilikSatis: "",
    alisFiyat: "",
    satisFiyat: "",
    tlFiyat: "",
    not: "",
    barkodOtomatikBasilsinMi: true,
  };

  const [alisForm, setAlisForm] = useState(emptyAlisForm);

  /* =========================================================
     SATIŞ PANEL FORM
  ========================================================= */
  const emptySatisForm = {
    barkod: "",
    tarih: getNowDateTimeLocal(),
    stokId: "",
    gram: "",
    ayar: "",
    milyem: "",
    olcu: "",
    iscilikAlis: "",
    iscilikSatis: "",
    alisFiyat: "",
    satisFiyat: "",
    hasFiyati: "",
    tlFiyati: "",
    hesapId: "",
    not: "",
    barkodluStokId: null,
  };

  const [satisForm, setSatisForm] = useState(emptySatisForm);

  /* =========================================================
     DATA LOAD
  ========================================================= */
  const loadStoklar = async () => {
    try {
      const resp = await axiosInstance.get("/stoklar");
      const data = resp.data || [];

      const gramStoklar = data.filter(
        (s) => String(s?.birim || "").toUpperCase() === "GRAM"
      );

      setStokOptions(gramStoklar);
    } catch (e) {
      console.error("Stoklar alınamadı:", e);
      setStokOptions([]);
    }
  };

  const loadHesaplar = async () => {
    try {
      const resp = await axiosInstance.get("/hesaplar");
      setHesapOptions(resp.data || []);
    } catch (e) {
      console.error("Hesaplar alınamadı:", e);
      setHesapOptions([]);
    }
  };

  const loadRows = async () => {
    setLoading(true);
    setError("");

    try {
      const resp = await axiosInstance.get("/barkodlu-stoklar", {
        params: {
          alisTarih: filters.alisTarih || null,
          satisTarih: filters.satisTarih || null,
          stokAdi: filters.stokAdi || null,
        },
      });

      const data = (resp.data || []).map((r) => ({
        ...r,
        id: r.id,
      }));

      setRows(data);
    } catch (e) {
      console.error("Barkodlu stok listesi alınamadı:", e);
      setRows([]);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Barkodlu stok listesi alınamadı."
      );
    } finally {
      setLoading(false);
    }
  };

  const initPage = async () => {
    await Promise.all([loadStoklar(), loadHesaplar(), loadRows()]);
  };

  useEffect(() => {
    if (yetkisiz || yetkiler === null) return;
    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yetkisiz, yetkiler]);

  /* =========================================================
     RFID / BARKOD OKUMA
     - Bu alan şimdilik klavye-emülasyon barkod okuyucu ile de çalışır.
     - Sonra WebSocket / serial / native bridge ile gerçek RFID akışına bağlarız.
  ========================================================= */
  const fillSalePanelFromBarcode = useCallback(async (barcodeValue) => {
    if (!barcodeValue) return;

    try {
      setSaleLoading(true);
      setError("");
      setSuccess("");

      const resp = await axiosInstance.get(`/barkodlu-stoklar/barcode/${barcodeValue}`);
      const data = resp.data;

      setSatisForm({
        barkod: data?.barkod || "",
        tarih: getNowDateTimeLocal(),
        stokId: data?.stokId || "",
        gram: data?.gram ?? "",
        ayar: data?.ayar ?? "",
        milyem: data?.milyem ?? "",
        olcu: data?.olcu ?? "",
        iscilikAlis: data?.iscilikAlis ?? "",
        iscilikSatis: data?.iscilikSatis ?? "",
        alisFiyat: data?.alisFiyat ?? "",
        satisFiyat: data?.satisFiyat ?? "",
        hasFiyati: data?.hasFiyati ?? "",
        tlFiyati: data?.tlFiyati ?? "",
        hesapId: "",
        not: data?.not || "",
        barkodluStokId: data?.id || null,
      });

      setSuccess(`Barkod okundu: ${barcodeValue}`);
    } catch (e) {
      console.error("Barkod okunamadı:", e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Okutulan barkoda ait kayıt bulunamadı."
      );
    } finally {
      setSaleLoading(false);
    }
  }, []);

  const handleManualBarcodeRead = useCallback(async () => {
    if (!satisForm.barkod?.trim()) {
      setError("Lütfen barkod girin veya okutun.");
      return;
    }
    await fillSalePanelFromBarcode(satisForm.barkod.trim());
  }, [satisForm.barkod, fillSalePanelFromBarcode]);

  useEffect(() => {
    let scannerBuffer = "";
    let lastKeyTime = 0;
    let scanTimeout = null;

    const resetScannerBuffer = () => {
      scannerBuffer = "";
      lastKeyTime = 0;
      if (scanTimeout) {
        clearTimeout(scanTimeout);
        scanTimeout = null;
      }
    };

    const processScannedBarcode = async (barcode) => {
      const temiz = (barcode || "").trim();
      if (!temiz) return;

      setSatisForm((prev) => ({
        ...prev,
        barkod: temiz,
      }));

      await fillSalePanelFromBarcode(temiz);
    };

    const handleKeyDown = async (e) => {
      // F8 ile barkod alanına odaklanma yine kalsın
      if (e.key === "F8") {
        e.preventDefault();
        barkodInputRef.current?.focus();
        return;
      }

      // Modal/input içinde kullanıcı normal yazı yazıyorsa elle karışmayalım
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      const isTypingField =
        tag === "textarea" ||
        (tag === "input" &&
          active !== barkodInputRef.current);

      // Eğer barkod input dışındaki bir input'ta kullanıcı yazıyorsa scanner logic devre dışı
      if (isTypingField) return;

      const now = Date.now();

      // Çok yavaşsa yeni barkod gibi başlat
      if (lastKeyTime && now - lastKeyTime > 120) {
        scannerBuffer = "";
      }
      lastKeyTime = now;

      if (e.key === "Enter") {
        if (scannerBuffer.trim().length >= 4) {
          e.preventDefault();
          const scanned = scannerBuffer;
          resetScannerBuffer();
          await processScannedBarcode(scanned);
        } else if (barkodInputRef.current === active) {
          e.preventDefault();
          await handleManualBarcodeRead();
        }
        return;
      }

      if (e.key === "Shift" || e.key === "Control" || e.key === "Alt" || e.key === "Tab") {
        return;
      }

      if (e.key.length === 1) {
        scannerBuffer += e.key;
      }

      if (scanTimeout) clearTimeout(scanTimeout);
      scanTimeout = setTimeout(() => {
        resetScannerBuffer();
      }, 250);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (scanTimeout) clearTimeout(scanTimeout);
    };
  }, [fillSalePanelFromBarcode, handleManualBarcodeRead]);

  /* =========================================================
     FORM CHANGE
  ========================================================= */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAlisChange = (field, value) => {
    setAlisForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSatisChange = (field, value) => {
    setSatisForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* =========================================================
     FİLTRE İŞLEMLERİ
  ========================================================= */
  const handleTemizleAlisTarih = () => {
    setFilters((prev) => ({
      ...prev,
      alisTarih: getTodayDate(),
    }));
  };

  const handleTemizleSatisTarih = () => {
    setFilters((prev) => ({
      ...prev,
      satisTarih: getTodayDate(),
    }));
  };

  const handleFiltrele = async () => {
    await loadRows();
  };

  const handleFiltreleriSifirla = async () => {
    const next = {
      alisTarih: getTodayDate(),
      satisTarih: getTodayDate(),
      stokAdi: "",
    };
    setFilters(next);

    setTimeout(() => {
      loadRows();
    }, 0);
  };

  /* =========================================================
     ALIŞ KAYDET
  ========================================================= */
  const validateAlisForm = () => {
    if (!alisForm.tarih) return "Alış tarihi zorunludur.";
    if (!alisForm.stokId) return "Stok seçimi zorunludur.";
    if (alisForm.gram === "" || Number(alisForm.gram) <= 0)
      return "Gram zorunludur.";
    return "";
  };

  const handleAlisKaydet = async () => {
    const err = validateAlisForm();
    if (err) {
      setError(err);
      return;
    }

    try {
      setSaveLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        tarih: alisForm.tarih,
        stokId: Number(alisForm.stokId),
        gram: safeNumber(alisForm.gram),
        ayar: safeNumber(alisForm.ayar),
        milyem: safeNumber(alisForm.milyem),
        olcu: safeNumber(alisForm.olcu),
        iscilikAlis: safeNumber(alisForm.iscilikAlis),
        iscilikSatis: safeNumber(alisForm.iscilikSatis),
        alisFiyat: safeNumber(alisForm.alisFiyat),
        satisFiyat: safeNumber(alisForm.satisFiyat),
        tlFiyat: safeNumber(alisForm.tlFiyat),
        not: alisForm.not || "",
        barkodOtomatikBasilsinMi: !!alisForm.barkodOtomatikBasilsinMi,
      };

      await axiosInstance.post("/barkodlu-stoklar", payload);

      setSuccess("Barkodlu alış kaydı başarıyla oluşturuldu.");
      setAlisForm({
        ...emptyAlisForm,
        tarih: getNowDateTimeLocal(),
      });

      await loadRows();
    } catch (e) {
      console.error("Barkodlu alış kaydı oluşturulamadı:", e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Barkodlu alış kaydı oluşturulamadı."
      );
    } finally {
      setSaveLoading(false);
    }
  };

  /* =========================================================
     SATIŞ ONAYLA
  ========================================================= */
  const validateSatisForm = () => {
    if (!satisForm.barkod?.trim()) return "Barkod zorunludur.";
    if (!satisForm.hesapId) return "Hesap seçimi zorunludur.";
    if (!satisForm.barkodluStokId) return "Satılacak barkodlu stok seçilemedi.";
    return "";
  };

  const handleSatisOnayla = async () => {
    const err = validateSatisForm();
    if (err) {
      setError(err);
      return;
    }

    try {
      setSaleLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        barkodluStokId: Number(satisForm.barkodluStokId),
        barkod: satisForm.barkod,
        tarih: satisForm.tarih,
        hesapId: Number(satisForm.hesapId),
        hasFiyati: safeNumber(satisForm.hasFiyati),
        tlFiyati: safeNumber(satisForm.tlFiyati),
        satisFiyat: safeNumber(satisForm.satisFiyat),
      };

      await axiosInstance.post("/barkodlu-stoklar/satis-onayla", payload);

      setSuccess("Satış işlemi başarıyla onaylandı.");
      setSatisForm({
        ...emptySatisForm,
        tarih: getNowDateTimeLocal(),
      });

      await loadRows();
    } catch (e) {
      console.error("Satış onayı başarısız:", e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Satış onayı başarısız."
      );
    } finally {
      setSaleLoading(false);
    }
  };

  /* =========================================================
     TEKİL BARKOD YAZDIR
  ========================================================= */
  const handlePrintBarcode = async (row) => {
    try {
      setPrintingId(row.id);
      setError("");
      setSuccess("");

      await axiosInstance.post(`/barkodlu-stoklar/${row.id}/print`);

      setSuccess(`Barkod yazdırma komutu gönderildi: ${row.barkod || ""}`);
    } catch (e) {
      console.error("Barkod yazdırılamadı:", e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Barkod yazdırılamadı."
      );
    } finally {
      setPrintingId(null);
    }
  };

  const handleTestPrint = async () => {
    try {
      setTestPrintLoading(true);
      setError("");
      setSuccess("");

      await axiosInstance.post("/barkodlu-stoklar/test-print", {
        barkod: "RF1748263912211",
        stokAdi: "14 AYAR YUZUK",
        gram: 5.250,
        ayar: 14,
        tlFiyati: 18750.00,
      });

      setSuccess("Test barkod yazdırma komutu gönderildi.");
    } catch (e) {
      console.error("Test barkod yazdırılamadı:", e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Test barkod yazdırılamadı."
      );
    } finally {
      setTestPrintLoading(false);
    }
  };

  /* =========================================================
     GRID
  ========================================================= */
  const columns = useMemo(
    () => [
      {
        field: "barkod",
        headerName: "Barkod",
        minWidth: 160,
        flex: 1.1,
      },
      {
        field: "stokAdi",
        headerName: "Stok Adı",
        minWidth: 160,
        flex: 1.2,
      },
      {
        field: "gram",
        headerName: "Gram",
        minWidth: 100,
        renderCell: (p) => formatNumberTR(p.row.gram, 3),
      },
      {
        field: "milyem",
        headerName: "Milyem",
        minWidth: 95,
        renderCell: (p) => formatNumberTR(p.row.milyem, 3),
      },
      {
        field: "iscilikAlis",
        headerName: "İşç. A.",
        minWidth: 95,
        renderCell: (p) => formatNumberTR(p.row.iscilikAlis, 2),
      },
      {
        field: "iscilikSatis",
        headerName: "İşç. S.",
        minWidth: 95,
        renderCell: (p) => formatNumberTR(p.row.iscilikSatis, 2),
      },
      {
        field: "ayar",
        headerName: "Ayar",
        minWidth: 80,
      },
      {
        field: "olcu",
        headerName: "Ölçü",
        minWidth: 90,
        renderCell: (p) => formatNumberTR(p.row.olcu, 2),
      },
      {
        field: "hasFiyati",
        headerName: "HasGr",
        minWidth: 95,
        renderCell: (p) => formatNumberTR(p.row.hasFiyati, 3),
      },
      {
        field: "alisTarih",
        headerName: "Alış Tar.",
        minWidth: 150,
        flex: 1,
        renderCell: (p) => formatDateTimeTR(p.row.alisTarih),
      },
      {
        field: "satildiMi",
        headerName: "Satıldı Mı",
        minWidth: 120,
        renderCell: (p) =>
          p.row.satildiMi ? (
            <Chip
              label="Satıldı"
              size="small"
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: 700,
              }}
            />
          ) : (
            <Chip
              label="Alındı"
              size="small"
              sx={{
                backgroundColor: "#f0e4b6",
                color: "#7a5511",
                fontWeight: 700,
              }}
            />
          ),
      },
      {
        field: "satisTarih",
        headerName: "Satış Tar.",
        minWidth: 150,
        flex: 1,
        renderCell: (p) => formatDateTimeTR(p.row.satisTarih),
      },
      {
        field: "tlFiyati",
        headerName: "TL Fiyatı",
        minWidth: 110,
        renderCell: (p) => formatNumberTR(p.row.tlFiyati, 2),
      },
      {
        field: "barkont",
        headerName: "Barkont",
        minWidth: 100,
        renderCell: (p) => p.row.barkont || "",
      },
      {
        field: "alisFiyat",
        headerName: "Alış Fiyat",
        minWidth: 110,
        renderCell: (p) => formatNumberTR(p.row.alisFiyat, 2),
      },
      {
        field: "satisFiyat",
        headerName: "Satış Fiyat",
        minWidth: 110,
        renderCell: (p) => formatNumberTR(p.row.satisFiyat, 2),
      },
      {
        field: "hesapAdi",
        headerName: "Hesap Adı",
        minWidth: 150,
        flex: 1,
      },
      {
        field: "not",
        headerName: "Not",
        minWidth: 180,
        flex: 1.2,
      },
      {
        field: "barkodYazdir",
        headerName: "Barkod Yazdır",
        minWidth: 145,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Print />}
            disabled={printingId === p.row.id}
            onClick={(e) => {
              e.stopPropagation();
              handlePrintBarcode(p.row);
            }}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              borderColor: "#b8860b",
              color: "#b8860b",
              fontWeight: 700,
              "&:hover": {
                borderColor: "#8f6a06",
                backgroundColor: "rgba(184,134,11,0.08)",
              },
            }}
          >
            {printingId === p.row.id ? "..." : "Yazdır"}
          </Button>
        ),
      },
    ],
    [printingId]
  );

  return (
    <div className="main-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />

      <div className="content barkodlu-page-content">
        {yetkiler === null ? (
          <h2 style={{ color: "#b8860b" }}>Yetkiler kontrol ediliyor...</h2>
        ) : yetkisiz ? (
          <YetkisizErisim sayfaAdi="Barkodlu İşlemler" />
        ) : (
          <>
            <div className="barkodlu-page-header">
              <h1 className="barkodlu-page-title">
                <MdQrCodeScanner style={{ marginRight: 8 }} />
                Barkodlu İşlemler
              </h1>

              <div className="barkodlu-page-actions">
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={initPage}
                  sx={{
                    textTransform: "none",
                    borderRadius: "12px",
                    borderColor: "#b8860b",
                    color: "#b8860b",
                    fontWeight: 700,
                  }}
                >
                  Yenile
                </Button>
                <Button
                    variant="contained"
                    startIcon={
                      testPrintLoading ? <CircularProgress size={16} color="inherit" /> : <Print />
                    }
                    onClick={handleTestPrint}
                    disabled={testPrintLoading}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #d4af37, #b8860b)",
                      color: "#fff",
                      fontWeight: 800,
                      boxShadow: "0 8px 20px rgba(184,134,11,0.18)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #c59a15, #976d06)",
                      },
                    }}
                  >
                    {testPrintLoading ? "Yazdırılıyor..." : "Test Barkod Yazdır"}
                  </Button>
              </div>
            </div>

            {!!error && (
              <Alert
                severity="error"
                sx={{ mb: 2, borderRadius: "14px" }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            {!!success && (
              <Alert
                severity="success"
                sx={{ mb: 2, borderRadius: "14px" }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}

            {/* ================= FILTER PANEL ================= */}
            <Paper className="barkodlu-filter-card">
              <div className="barkodlu-card-title-row">
                <Typography className="barkodlu-card-title">
                  <Inventory2 fontSize="small" />
                  Liste Filtreleri
                </Typography>
              </div>

              <Grid container spacing={2.2}>
                <Grid item xs={12} md={4}>
                  <div className="barkodlu-filter-inline">
                    <TextField
                      label="Alış Tarihi"
                      type="date"
                      fullWidth
                      value={filters.alisTarih}
                      onChange={(e) =>
                        handleFilterChange("alisTarih", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleTemizleAlisTarih}
                      className="mini-filter-btn"
                    >
                      Temizle
                    </Button>
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <div className="barkodlu-filter-inline">
                    <TextField
                      label="Satış Tarihi"
                      type="date"
                      fullWidth
                      value={filters.satisTarih}
                      onChange={(e) =>
                        handleFilterChange("satisTarih", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleTemizleSatisTarih}
                      className="mini-filter-btn"
                    >
                      Temizle
                    </Button>
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Stok Adı"
                    fullWidth
                    value={filters.stokAdi}
                    onChange={(e) =>
                      handleFilterChange("stokAdi", e.target.value)
                    }
                    placeholder="Barkodlu stok adına göre ara"
                  />
                </Grid>
              </Grid>

              <div className="barkodlu-filter-bottom-actions">
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={handleFiltrele}
                  className="gold-filled-btn"
                >
                  Filtrele
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Autorenew />}
                  onClick={handleFiltreleriSifirla}
                  className="gold-outlined-btn"
                >
                  Sıfırla
                </Button>
              </div>
            </Paper>

            {/* ================= PANELS ================= */}
            <div className="barkodlu-panels-grid">
              {/* ALIŞ PANEL */}
              <Paper className="barkodlu-panel-card">
                <div className="barkodlu-card-title-row">
                  <Typography className="barkodlu-card-title">
                    <Add fontSize="small" />
                    Alış Paneli
                  </Typography>
                </div>

                <Grid container spacing={2} className="barkodlu-form-grid">
                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Tarih"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      value={alisForm.tarih}
                      onChange={(e) => handleAlisChange("tarih", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-full">
                    <TextField
                      select
                      label="Stok"
                      fullWidth
                      size="small"
                      value={alisForm.stokId}
                      onChange={(e) => handleAlisChange("stokId", e.target.value)}
                    >
                      {stokOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.stokAdi}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-full barkodlu-select-wide">
                    <TextField
                      label="Gram"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.gram}
                      onChange={(e) => handleAlisChange("gram", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Ayar"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.ayar}
                      onChange={(e) => handleAlisChange("ayar", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Milyem"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.milyem}
                      onChange={(e) => handleAlisChange("milyem", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Ölçü"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.olcu}
                      onChange={(e) => handleAlisChange("olcu", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="İşçilik Alış"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.iscilikAlis}
                      onChange={(e) => handleAlisChange("iscilikAlis", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="İşçilik Satış"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.iscilikSatis}
                      onChange={(e) => handleAlisChange("iscilikSatis", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Alış"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.alisFiyat}
                      onChange={(e) => handleAlisChange("alisFiyat", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Satış"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.satisFiyat}
                      onChange={(e) => handleAlisChange("satisFiyat", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} className="barkodlu-field-full">
                    <TextField
                      label="TL Fiyat"
                      type="number"
                      fullWidth
                      size="small"
                      value={alisForm.tlFiyat}
                      onChange={(e) => handleAlisChange("tlFiyat", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} className="barkodlu-field-full">
                    <TextField
                      label="Not"
                      fullWidth
                      multiline
                      minRows={3}
                      value={alisForm.not}
                      onChange={(e) => handleAlisChange("not", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} className="barkodlu-field-full">
                    <div className="barkodlu-switch-row">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={alisForm.barkodOtomatikBasilsinMi}
                            onChange={(e) =>
                              handleAlisChange(
                                "barkodOtomatikBasilsinMi",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Barkod otomatik basılsın mı?"
                      />
                    </div>
                  </Grid>
                </Grid>

                <div className="barkodlu-panel-footer">
                  <Button
                    variant="contained"
                    startIcon={saveLoading ? <CircularProgress size={16} /> : <Add />}
                    onClick={handleAlisKaydet}
                    disabled={saveLoading}
                    className="gold-filled-btn"
                  >
                    Kaydet
                  </Button>
                </div>
              </Paper>

              {/* SATIŞ PANEL */}
              <Paper className="barkodlu-panel-card">
                <div className="barkodlu-card-title-row">
                  <Typography className="barkodlu-card-title">
                    <Sell fontSize="small" />
                    Satış Paneli
                  </Typography>
                </div>

                <Grid container spacing={2} className="barkodlu-form-grid">
                  <Grid item xs={12} className="barkodlu-field-full">
                    <TextField
                      label="Barkod"
                      fullWidth
                      size="small"
                      inputRef={barkodInputRef}
                      value={satisForm.barkod}
                      onChange={(e) => handleSatisChange("barkod", e.target.value)}
                      placeholder="RFID / Barkod okut veya elle gir"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleManualBarcodeRead();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleManualBarcodeRead}>
                              <MdQrCodeScanner />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} className="barkodlu-field-full">
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={
                        saleLoading ? <CircularProgress size={16} /> : <MdQrCodeScanner />
                      }
                      onClick={handleManualBarcodeRead}
                      className="gold-outlined-btn barkodlu-full-btn"
                    >
                      Barkodu Oku / Getir
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Tarih"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      value={satisForm.tarih}
                      onChange={(e) => handleSatisChange("tarih", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      select
                      label="Stok"
                      fullWidth
                      size="small"
                      value={satisForm.stokId}
                      onChange={(e) => handleSatisChange("stokId", e.target.value)}
                    >
                      {stokOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.stokAdi}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Gram"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.gram}
                      onChange={(e) => handleSatisChange("gram", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Ayar"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.ayar}
                      onChange={(e) => handleSatisChange("ayar", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Milyem"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.milyem}
                      onChange={(e) => handleSatisChange("milyem", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Ölçü"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.olcu}
                      onChange={(e) => handleSatisChange("olcu", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="İşçilik Alış"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.iscilikAlis}
                      onChange={(e) => handleSatisChange("iscilikAlis", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="İşçilik Satış"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.iscilikSatis}
                      onChange={(e) => handleSatisChange("iscilikSatis", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Alış"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.alisFiyat}
                      onChange={(e) => handleSatisChange("alisFiyat", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Satış"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.satisFiyat}
                      onChange={(e) => handleSatisChange("satisFiyat", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="Has Fiyatı"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.hasFiyati}
                      onChange={(e) => handleSatisChange("hasFiyati", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} className="barkodlu-field-half">
                    <TextField
                      label="TL Fiyatı"
                      type="number"
                      fullWidth
                      size="small"
                      value={satisForm.tlFiyati}
                      onChange={(e) => handleSatisChange("tlFiyati", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} className="barkodlu-field-full">
                    <TextField
                      select
                      label="Hesap"
                      fullWidth
                      size="small"
                      value={satisForm.hesapId}
                      onChange={(e) => handleSatisChange("hesapId", e.target.value)}
                    >
                      {hesapOptions.map((h) => (
                        <MenuItem key={h.id} value={h.id}>
                          {h.hesapAdi}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <div className="barkodlu-panel-footer">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={
                      saleLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CheckCircle />
                      )
                    }
                    onClick={handleSatisOnayla}
                    disabled={saleLoading}
                    className="green-filled-btn"
                  >
                    Satış Onayla
                  </Button>
                </div>
              </Paper>
            </div>

            {/* ================= GRID ================= */}
            <Paper className="barkodlu-grid-card">
              <div className="barkodlu-card-title-row">
                <Typography className="barkodlu-card-title">
                  <Inventory2 fontSize="small" />
                  Barkodlu Stok Listesi
                </Typography>
              </div>

              {loading ? (
                <Box className="barkodlu-loading-box">
                  <CircularProgress />
                </Box>
              ) : (
                <ProsetDataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={8}
                  noRowsLabel="Barkodlu stok kaydı bulunamadı"
                />
              )}
            </Paper>
          </>
        )}
      </div>
    </div>
  );
};

export default KuyumcuBarkodluIslemlerPage;